import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Badge, Pagination, Form, Row, Col, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../../utils/axiosConfig';
import { useAuth } from '../../../contexts/AuthContext';
import './VehicleManagementTable.css';

const VehicleManagementTable = () => {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [rejectComment, setRejectComment] = useState('');
    const [rejectionType, setRejectionType] = useState('rejected_for_edit');

    const navigate = useNavigate();
    const { hasRole } = useAuth();

    const fetchVehicles = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append('page', currentPage);
            if (searchTerm) params.append('search', searchTerm);
            if (categoryFilter) params.append('category', categoryFilter);
            if (statusFilter) params.append('status', statusFilter);

            const response = await axiosInstance.get(`/vehicles?${params.toString()}`);

            if (response.data && response.data.data) {
                setVehicles(response.data.data);
                setTotalPages(response.data.last_page || 1);
            }
        } catch (error) {
            console.error('Error fetching vehicles:', error);
            toast.error('Error loading vehicles.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVehicles();
    }, [currentPage, searchTerm, categoryFilter, statusFilter]);

    const getStatusBadge = (status) => {
        const statusMapping = {
            approved: { text: 'Approved', variant: 'success' },
            pending: { text: 'Pending', variant: 'warning' },
            rejected: { text: 'Rejected', variant: 'danger' },
            rejected_for_edit: { text: 'Needs Revision', variant: 'info' }
        };
        const { text, variant } = statusMapping[status] || { text: status, variant: 'secondary' };
        return <Badge bg={variant}>{text}</Badge>;
    };

    const handleView = (vehicleId) => {
        navigate(`/vehicle-management/view/${vehicleId}`);
    };

    const handleEdit = (vehicleId) => {
        navigate(`/vehicle-management/edit/${vehicleId}`);
    };

    const handleApprove = async (vehicleId) => {
        try {
            await axiosInstance.post(`/vehicles/${vehicleId}/approve`, {
                comment: 'Approved'
            });
            toast.success('Vehicle approved successfully!');
            fetchVehicles();
        } catch (error) {
            console.error('Error approving vehicle:', error);
            toast.error(error.response?.data?.message || 'Failed to approve vehicle');
        }
    };

    const openRejectModal = (vehicle) => {
        setSelectedVehicle(vehicle);
        setRejectComment('');
        setRejectionType('rejected_for_edit');
        setShowRejectModal(true);
    };

    const handleReject = async () => {
        if (rejectComment.length < 10) {
            toast.error('Please provide a reason with at least 10 characters');
            return;
        }

        try {
            await axiosInstance.post(`/vehicles/${selectedVehicle.vehicle_id}/reject`, {
                comment: rejectComment,
                rejection_type: rejectionType
            });
            toast.success('Vehicle rejected successfully!');
            setShowRejectModal(false);
            fetchVehicles();
        } catch (error) {
            console.error('Error rejecting vehicle:', error);
            toast.error(error.response?.data?.message || 'Failed to reject vehicle');
        }
    };

    const handleDelete = async (vehicleId) => {
        if (!window.confirm('Are you sure you want to delete this vehicle?')) return;

        try {
            await axiosInstance.delete(`/vehicles/${vehicleId}`);
            toast.success('Vehicle deleted successfully!');
            fetchVehicles();
        } catch (error) {
            console.error('Error deleting vehicle:', error);
            toast.error(error.response?.data?.message || 'Failed to delete vehicle');
        }
    };

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const clearFilters = () => {
        setSearchTerm('');
        setCategoryFilter('');
        setStatusFilter('');
        setCurrentPage(1);
    };

    if (loading) return <div>Loading vehicles...</div>;

    return (
        <div className="table-container">
            <Form className="mb-3">
                <Row>
                    <Col md={3}>
                        <Form.Control
                            type="search"
                            placeholder="Search by Vehicle Name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="input-brand-shadow"
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="input-brand-shadow"
                        >
                            <option value="">All Categories</option>
                            <option value="Sedan">Sedan</option>
                            <option value="Hatchback">Hatchback</option>
                            <option value="SUV/Crossover">SUV/Crossover</option>
                            <option value="Commercial">Commercial</option>
                        </Form.Select>
                    </Col>
                    <Col md={3}>
                        <Form.Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="input-brand-shadow"
                        >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="rejected_for_edit">Needs Revision</option>
                        </Form.Select>
                    </Col>
                    <Col md={3} className="text-end">
                        <Button variant="secondary" onClick={clearFilters}>
                            Clear Filters
                        </Button>
                    </Col>
                </Row>
            </Form>

            <div className="table-brand-shadow">
                <Table responsive striped bordered hover className="text-center">
                    <thead className="brand-table-head">
                        <tr>
                            <th>Sr. No</th>
                            <th>Vehicle ID</th>
                            <th>Title (EN)</th>
                            <th>Category</th>
                            <th>Status</th>
                            <th>Created By</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vehicles.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="text-center">No vehicles found</td>
                            </tr>
                        ) : (
                            vehicles.map((vehicle, index) => (
                                <tr key={vehicle.id}>
                                    <td>{(currentPage - 1) * 50 + index + 1}</td>
                                    <td>{vehicle.vehicle_id}</td>
                                    <td>{vehicle.title_en}</td>
                                    <td>{vehicle.category}</td>
                                    <td>{getStatusBadge(vehicle.status)}</td>
                                    <td>{vehicle.creator?.name || '-'}</td>
                                    <td>
                                        <div className="d-flex gap-1 justify-content-center flex-wrap">
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => handleView(vehicle.vehicle_id)}
                                            >
                                                View
                                            </Button>
                                            {/* <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => handleEdit(vehicle.vehicle_id)}
                                            >
                                                Edit
                                            </Button> */}

                                            {hasRole('it') && vehicle.status === 'pending' && (
                                                <>
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={() => handleApprove(vehicle.vehicle_id)}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => openRejectModal(vehicle)}
                                                    >
                                                        Reject
                                                    </Button>
                                                </>
                                            )}

                                            {hasRole('it') && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(vehicle.vehicle_id)}
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>

            <Pagination className="justify-content-end">
                {[...Array(totalPages)].map((_, i) => (
                    <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => handlePageChange(i + 1)}
                    >
                        {i + 1}
                    </Pagination.Item>
                ))}
            </Pagination>

            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Reject Vehicle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Reason for rejection (min 10 characters)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={rejectComment}
                            onChange={(e) => setRejectComment(e.target.value)}
                            placeholder="Please provide a reason for rejection..."
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Rejection Type</Form.Label>
                        <Form.Select
                            value={rejectionType}
                            onChange={(e) => setRejectionType(e.target.value)}
                        >
                            <option value="rejected_for_edit">Return for Edit</option>
                            <option value="rejected">Permanently Reject</option>
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleReject}>
                        Submit Rejection
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default VehicleManagementTable;
